import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { registerSW } from 'virtual:pwa-register'
import App from './App.jsx'
import './App.css'
import { ThemeProvider } from './contexts/ThemeContext.jsx'
import { FlashFonicProvider } from './contexts/FlashFonicContext.js'
import { initFirebase, getAuthInstance, signInAnonymously, waitForUserAvailable, completeMagicLinkIfPresent, completeGoogleRedirectIfPresent, setDisableAutoAnon, trackEvent } from './firebase/config.js'
import { initPixels, trackPixels } from './analytics/pixels.js'
import { markOnboardingNeeded } from './components/OnboardingWizard.jsx'
import { ENV } from './config/env.js'

// Registered manually (injectRegister: false in vite.config.js) so a failed registration
// can be caught instead of becoming an unhandled promise rejection — in-app browsers
// (Facebook/Instagram/TikTok's built-in WebViews, some older mobile Safari) frequently
// restrict or break the Service Worker API entirely. The app works fine without one; only
// offline caching/PWA install is unavailable in those browsers.
if ('serviceWorker' in navigator) {
  registerSW({
    onRegisterError(error) {
      console.warn('[PWA] Service worker registration failed (expected in some in-app browsers):', error);
    },
  });
}

if (ENV.analytics.sentryDsn) {
  Sentry.init({
    dsn: ENV.analytics.sentryDsn,
    environment: ENV.isProd ? 'production' : 'development',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('ErrorBoundary caught an error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '24px', color: 'white' }}>
          <h2>Something went wrong.</h2>
          <p>Try reloading the page. If the problem persists, please contact support.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

// Initialize Firebase ASAP and handle magic link BEFORE creating an anonymous session
(async () => {
  try { setDisableAutoAnon(true); } catch (_) {}
  try {
    // Initialize pixels as early as possible
    try { initPixels(); } catch (_) {}
    await initFirebase();
    try { await completeMagicLinkIfPresent(); } catch (_) {}
    // Picks up a signInWithGoogle() that redirected instead of using a popup — see the
    // in-app-browser branch in firebase/config.js. Must run before the anonymous-signin
    // decision below so a completed Google sign-in isn't masked by a fresh guest session.
    try {
      const redirectResult = await completeGoogleRedirectIfPresent();
      if (redirectResult?.additionalUserInfo?.isNewUser) {
        try { trackEvent('sign_up', { method: 'google' }); } catch (_) {}
        try { trackPixels('CompleteRegistration'); } catch (_) {}
        try { markOnboardingNeeded(); } catch (_) {}
      } else if (redirectResult) {
        try { trackEvent('login', { method: 'google' }); } catch (_) {}
      }
    } catch (_) {}
  } catch (_) {}
  finally { try { setDisableAutoAnon(false); } catch (_) {} }
  
  // Check if user is returning from Stripe or other authenticated flow
  const isReturningFromStripe = (() => {
    try {
      const path = window.location.pathname;
      const search = window.location.search;
      return path === '/success' || path === '/cancel' || search.includes('session_id=') || search.includes('stripe=return');
    } catch(_) { return false; }
  })();
  
  try {
    const a = getAuthInstance();
    // If returning from Stripe, wait much longer for auth to restore and never sign in anonymously
    const waitTime = isReturningFromStripe ? 8000 : 3500;
    try { await waitForUserAvailable(waitTime); } catch (_) {}
    const hasAccountHint = (() => { try { return !!localStorage.getItem('ff:hasAccount'); } catch(_) { return false; } })();
    
    // Only sign in anonymously if:
    // 1. Not returning from Stripe/payment flow
    // 2. No current user exists
    // 3. No account hint exists
    if (!isReturningFromStripe && a && !a.currentUser && !hasAccountHint) {
      try { await signInAnonymously(a); } catch (_) {}
      try { await waitForUserAvailable(2000); } catch (_) {}
    }
  } catch (_) {}
})();

// App itself constructs the provider with real values, so we can mount directly
ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ThemeProvider>
      <BrowserRouter>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </BrowserRouter>
    </ThemeProvider>
  </React.StrictMode>,
)
